// Role-based utility functions

export const USER_ROLES = {
  SUPER_ADMIN: 'super_admin',
  PROPERTY_ONBOARDING_STAFF: 'property-onboarding-staff',
  VIEWER: 'viewer',
  STAFF: 'staff',
  CONTRACTOR: 'contractor',
  CONTRACTOR_STAFF: 'contractor_staff',
  ADMIN: 'admin',
  DEMO: 'demo',
  CLOUD_STAFF: 'cloud_staff',
  BUILDING_MANAGER: 'building_manager',
  FM: 'fm'
};

export const getUserRole = () => {
  return localStorage.getItem('userRole');
};

export const isContractor = () => {
  return getUserRole() === USER_ROLES.CONTRACTOR;
};

export const isContractorAdmin = () => {
  if (getUserRole() !== USER_ROLES.CONTRACTOR) return false;
  return localStorage.getItem('isCompanyAdmin') === 'true';
};

export const isContractorStaff = () => {
  if (getUserRole() !== USER_ROLES.CONTRACTOR) return false;
  return localStorage.getItem('isCompanyAdmin') !== 'true';
};

export const isAdmin = () => {
  const role = getUserRole();
  return role === USER_ROLES.ADMIN || role === USER_ROLES.SUPER_ADMIN;
};

export const isSuperAdmin = () => {
  return getUserRole() === USER_ROLES.SUPER_ADMIN;
};

export const isFM = () => {
  const role = getUserRole();
  return ['staff', 'admin', 'fm', 'super_admin'].includes(role);
};

export const isPropertyOnboardingStaff = () => {
  return getUserRole() === USER_ROLES.PROPERTY_ONBOARDING_STAFF;
};

export const isStaff = () => {
  return getUserRole() === USER_ROLES.STAFF;
};

export const isViewer = () => {
  return getUserRole() === USER_ROLES.VIEWER;
};

/** Property owners register as "owner" and get the viewer role; use for owner-specific messaging or features */
export const isPropertyOwner = () => {
  return isViewer() || (typeof localStorage !== 'undefined' && localStorage.getItem('accountType') === 'owner');
};

export const isCloudStaff = () => {
  return getUserRole() === USER_ROLES.CLOUD_STAFF;
};

/** Internal ops account (e.g. cloudstaff@) — no tenant role in UI; JWT uses cloud_staff for API routing */
export const isInternalStaffAccount = () => {
  if (typeof localStorage === 'undefined') return false;
  return localStorage.getItem('internalStaffAccount') === 'true';
};

export const isBuildingManager = () => {
  return getUserRole() === USER_ROLES.BUILDING_MANAGER;
};

// Get appropriate dashboard route based on user role
export const getDashboardRoute = (role = null) => {
  const userRole = role || getUserRole();
  
  switch (userRole) {
    case USER_ROLES.CONTRACTOR:
      if (localStorage.getItem('isCompanyAdmin') === 'true') {
        return '/contractor/dashboard';
      }
      return '/contractor/staff-dashboard';
    case USER_ROLES.CLOUD_STAFF:
      return '/cloud-staff/dashboard';
    case USER_ROLES.BUILDING_MANAGER:
      return '/building-manager/dashboard';
    case USER_ROLES.ADMIN:
    case USER_ROLES.SUPER_ADMIN:
      return '/fm/dashboard';
    case USER_ROLES.FM:
    case USER_ROLES.STAFF:
      return '/fm/dashboard';
    case USER_ROLES.VIEWER:
    default:
      return '/dashboard';
  }
};

// Check if user has permission to access a specific feature
export const hasPermission = (requiredRoles = []) => {
  const userRole = getUserRole();
  return requiredRoles.includes(userRole);
};

// Get features available to each role
export const getRoleFeatures = (role = null) => {
  const userRole = role || getUserRole();
  
  switch (userRole) {
    case USER_ROLES.CONTRACTOR:
      return {
        canViewProperties: true,
        canEditProperties: false,
        canCreateProperties: false,
        canDeleteProperties: false,
        canViewTasks: true,
        canCompleteInspections: true,
        canGenerateReports: true,
        canManageUsers: false,
        canViewFullDashboard: false,
        allowedSections: ['tasks', 'inspections', 'reports']
      };
    case USER_ROLES.ADMIN:
      return {
        canViewProperties: true,
        canEditProperties: true,
        canCreateProperties: true,
        canDeleteProperties: true,
        canViewTasks: true,
        canCompleteInspections: true,
        canGenerateReports: true,
        canManageUsers: true,
        canViewFullDashboard: true,
        allowedSections: ['all']
      };
    case USER_ROLES.STAFF:
    case USER_ROLES.FM:
      return {
        canViewProperties: true,
        canEditProperties: true,
        canCreateProperties: false,
        canDeleteProperties: false,
        canViewTasks: true,
        canCompleteInspections: true,
        canGenerateReports: true,
        canManageUsers: false,
        canViewFullDashboard: true,
        allowedSections: ['properties', 'tasks', 'inspections', 'reports', 'safety']
      };
    case USER_ROLES.CLOUD_STAFF:
      return {
        canViewProperties: true,
        canEditProperties: false,
        canCreateProperties: false,
        canDeleteProperties: false,
        canViewTasks: false,
        canCompleteInspections: false,
        canGenerateReports: false,
        canManageUsers: false,
        canViewFullDashboard: false,
        canManageAssetCodes: true,
        allowedSections: ['asset-code-tool', 'activity-log']
      };
    case USER_ROLES.VIEWER:
    default:
      return {
        canViewProperties: true,
        canEditProperties: false,
        canCreateProperties: false,
        canDeleteProperties: false,
        canViewTasks: false,
        canCompleteInspections: false,
        canGenerateReports: false,
        canManageUsers: false,
        canViewFullDashboard: true,
        allowedSections: ['properties', 'reports']
      };
  }
}; 